import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Box, Button, HStack, IconButton, Spinner, Table, Text, VStack,} from "@chakra-ui/react";
import {HiArrowRight, HiOutlineCpuChip, HiStar} from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import {ModelService} from "@/services/ModelService";
import {fromApiResponse} from "@/services/jobOfferMapper";
import JobOfferFormModal from "@/components/jobOffer/JobOfferFormModal";
import {toaster} from "@/components/ui/toaster";
import {RecruitmentStatusBadge} from "@/pages/models/RecruitmentStatusBadge";
import CampaignsSection from "@/pages/models/CampaignsSection";

const isDraftOrInactive = (status) => ["draft", "inactive"].includes(status?.toLowerCase());

const ScenarioDetailPage = () => {
    const { id, scenarioId } = useParams();
    const navigate = useNavigate();

    const [agent, setAgent] = useState(null);
    const [scenario, setScenario] = useState(null);
    const [recruitments, setRecruitments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [jobOfferModal, setJobOfferModal] = useState(null);
    const [favLoadingId, setFavLoadingId] = useState(null);
    const [editLoadingId, setEditLoadingId] = useState(null);

    useSetPageHeader("Szczegóły scenariusza", "", <HiOutlineCpuChip />);

    const fetchRecruitments = () =>
        ModelService.getRecruitments(scenarioId).then((data) => setRecruitments(data?.items ?? []));

    useEffect(() => {
        setIsLoading(true);
        ModelService.getById(id).then((agentData) => {
            setAgent(agentData);
            const scenarioData = agentData?.scenarios?.find(s => s.id === scenarioId) ?? null;
            setScenario(scenarioData);

            if (agentData?.type === "SalesManager") {
                setIsLoading(false);
                return;
            }

            ModelService.getRecruitments(scenarioId).then((recruitmentsData) => {
                setRecruitments(recruitmentsData?.items ?? []);
                setIsLoading(false);
            });
        });
    }, [scenarioId]);

    const handleEndRecruitment = (recruitmentId) => {
        ModelService.endRecruitment(scenarioId, recruitmentId).then(() => {
            setRecruitments((prev) =>
                prev.map((r) =>
                    r.id === recruitmentId ? { ...r, status: "ended" } : r
                )
            );
        });
    };

    const handleToggleFavorite = async (id) => {
        setFavLoadingId(id);
        const updated = await ModelService.toggleFavorite(id);
        setRecruitments((prev) =>
            prev.map((r) => r.id === id ? { ...r, isFavorite: updated.isFavorite } : r)
        );
        setFavLoadingId(null);
    };

    const handleEditRecruitment = async (recruitmentId) => {
        setEditLoadingId(recruitmentId);
        try {
            const data = await ModelService.getRecruitmentById(recruitmentId);
            setJobOfferModal({ mode: "edit", recruitmentId, initialValues: fromApiResponse(data) });
        } finally {
            setEditLoadingId(null);
        }
    };

    const handleCopyLink = (chatPath) => {
        const link = `${window.location.origin}/c${chatPath}`;
        navigator.clipboard.writeText(link);
        toaster.create({ type: "success", title: "Skopiowano link do schowka" });
    };

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="brand.400" size="lg" />
            </VStack>
        );
    }

    if (!scenario) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="text.grey">Scenariusz nie został znaleziony.</Text>
            </VStack>
        );
    }

    return (
        <VStack w="full" py={6} gap={6} align="flex-start">
            <Box
                w="full"
                bg="bg.card"
                border="1px solid"
                borderColor="border.primary"
                borderRadius={12}
                p={5}
            >
                <HStack justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
                    <VStack align="flex-start" gap={2}>
                        <Text fontWeight={700} fontSize="xl" color="white">
                            {scenario.name}
                        </Text>
                        <HStack gap={4} flexWrap="wrap">
                            <Text color="text.grey" fontSize="sm">
                                Utworzono: {new Date(scenario.createdAtUtc).toLocaleDateString('pl-PL')}
                            </Text>
                            <Text color="text.grey" fontSize="sm">
                                Edytowano: {new Date(scenario.editedAtUtc).toLocaleDateString('pl-PL')}
                            </Text>
                        </HStack>
                    </VStack>
                    {agent?.type !== "SalesManager" && (
                        <Button variant="primary" flexShrink={0} onClick={() => setJobOfferModal({ mode: "create" })}>
                            Nowa rekrutacja
                        </Button>
                    )}
                </HStack>
            </Box>

            {agent?.type === "SalesManager" ? (
                <CampaignsSection agentId={id} agentName={agent?.name} scenarioId={scenarioId} />
            ) : (
            <VStack w="full" align="flex-start" gap={3}>
                <Text fontWeight={700} fontSize="lg" color="white">
                    Lista rekrutacji
                </Text>

                {/* Desktop table */}
                <Box
                    display={{ base: 'none', md: 'block' }}
                    w="full"
                    border="1px solid"
                    borderColor="border.primary"
                    borderRadius={12}
                    overflowX="auto"
                >
                    <Table.Root minW="720px">
                        <Table.Header>
                            <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                <Table.ColumnHeader
                                    color="text.grey" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    Nazwa stanowiska
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="text.grey" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    Status
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="text.grey" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    Numer WhatsApp
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="text.grey" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    Data utworzenia
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="text.grey" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none" textAlign="right"
                                >
                                    Akcje
                                </Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {recruitments.map((recruitment) => (
                                <Table.Row
                                    key={recruitment.id}
                                    bg="bg.secondary"
                                    borderBottom="1px solid"
                                    borderColor="border.primary"
                                    _hover={{ bg: "bg.highlight" }}
                                    transition="background 0.15s"
                                    onClick={() => navigate(`/models/${id}/recruitments/${recruitment.id}/candidates`)}
                                    cursor={'pointer'}
                                >
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="white" fontSize="sm" fontWeight={500}>
                                            {recruitment.jobTitle}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <RecruitmentStatusBadge status={recruitment.status} />
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="text.grey" fontSize="sm">-</Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="text.grey" fontSize="sm">
                                            {new Date(recruitment.createdAtUtc).toLocaleDateString('pl-PL')}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none" textAlign="right">
                                        <HStack gap={2} justify="flex-end">
                                            <IconButton
                                                variant="ghost"
                                                size="sm"
                                                aria-label="Ulubione"
                                                color={recruitment.isFavorite ? "yellow.400" : "gray.500"}
                                                loading={favLoadingId === recruitment.id}
                                                onClick={(e) => { e.stopPropagation(); handleToggleFavorite(recruitment.id); }}
                                            >
                                                <HiStar />
                                            </IconButton>
                                            {!isDraftOrInactive(recruitment.status) &&
                                                <Button
                                                    variant="outlined"
                                                    size="sm"
                                                    color="red.400"
                                                    borderColor="red.800"
                                                    _hover={{ bg: "red.950" }}
                                                    onClick={(e) => { e.stopPropagation(); handleEndRecruitment(recruitment.id); }}
                                                    disabled={recruitment.status === "ended"}
                                                >
                                                    Zakończ
                                                </Button>
                                            }
                                            {!isDraftOrInactive(recruitment.status) &&
                                                <Button
                                                    variant="outlined"
                                                    size="sm"
                                                    onClick={(e) => { e.stopPropagation(); handleCopyLink(recruitment.chatPath); }}
                                                >
                                                    Skopiuj link
                                                </Button>
                                            }
                                            <Button
                                                variant="outlined"
                                                size="sm"
                                                loading={editLoadingId === recruitment.id}
                                                onClick={(e) => { e.stopPropagation(); handleEditRecruitment(recruitment.id); }}
                                            >
                                                Edytuj
                                            </Button>
                                            <IconButton
                                                variant="outlined"
                                                size="sm"
                                                aria-label="Szczegóły rekrutacji"
                                                onClick={() => navigate(`/models/${id}/recruitments/${recruitment.id}/candidates`)}
                                            >
                                                <HiArrowRight />
                                            </IconButton>
                                        </HStack>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                </Box>

                {/* Mobile card list */}
                <VStack
                    display={{ base: 'flex', md: 'none' }}
                    w="full"
                    gap={2}
                >
                    {recruitments.map((recruitment) => (
                        <Box
                            key={recruitment.id}
                            w="full"
                            bg="bg.card"
                            border="1px solid"
                            borderColor="border.primary"
                            borderRadius={10}
                            p={4}
                            cursor="pointer"
                            onClick={() => navigate(`/models/${id}/recruitments/${recruitment.id}/candidates`)}
                            _active={{ opacity: 0.8 }}
                        >
                            <HStack justify="space-between" align="flex-start" mb={2}>
                                <VStack align="flex-start" gap={0} flex={1} minW={0}>
                                    <HStack gap={2} flexWrap="wrap" justifyContent="space-between" w={'full'}>
                                        <Text color="white" fontSize="sm" fontWeight={600}>
                                            {recruitment.jobTitle}
                                        </Text>
                                        <Box>
                                            <RecruitmentStatusBadge status={recruitment.status} />
                                            <IconButton
                                                variant="ghost"
                                                size="sm"
                                                aria-label="Ulubione"
                                                color={recruitment.isFavorite ? "yellow.400" : "gray.500"}
                                                loading={favLoadingId === recruitment.id}
                                                flexShrink={0}
                                                onClick={(e) => { e.stopPropagation(); handleToggleFavorite(recruitment.id); }}
                                            >
                                                <HiStar />
                                            </IconButton>
                                        </Box>
                                    </HStack>
                                    <Text color="text.grey" fontSize="xs">
                                        Data: {new Date(recruitment.createdAtUtc).toLocaleDateString('pl-PL')}
                                    </Text>
                                </VStack>
                            </HStack>
                            <HStack gap={2} justify="flex-end" flexWrap="wrap">
                                {!isDraftOrInactive(recruitment.status) &&
                                    <Button
                                        variant="outlined"
                                        size="sm"
                                        color="red.400"
                                        borderColor="red.800"
                                        _hover={{ bg: "red.950" }}
                                        onClick={(e) => { e.stopPropagation(); handleEndRecruitment(recruitment.id); }}
                                        disabled={recruitment.status === "ended"}
                                    >
                                        Zakończ
                                    </Button>
                                }
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    loading={editLoadingId === recruitment.id}
                                    onClick={(e) => { e.stopPropagation(); handleEditRecruitment(recruitment.id); }}
                                >
                                    Edytuj
                                </Button>
                                <IconButton
                                    variant="outlined"
                                    size="sm"
                                    aria-label="Szczegóły rekrutacji"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/models/${id}/recruitments/${recruitment.id}/candidates`); }}
                                >
                                    <HiArrowRight />
                                </IconButton>
                            </HStack>
                        </Box>
                    ))}
                </VStack>
            </VStack>
            )}

            {agent?.type !== "SalesManager" && jobOfferModal && (
                <JobOfferFormModal
                    onClose={() => setJobOfferModal(null)}
                    initialValues={jobOfferModal.initialValues}
                    onSubmit={(values, isDraft) => {
                        if (isDraft) {
                            return ModelService.saveDraft({ ...values, scenarioId: values.scenarioId ?? scenarioId }).then(fetchRecruitments);
                        }
                        if (jobOfferModal.mode === "create") {
                            return ModelService.createRecruitment(scenarioId, values).then(() => {
                                fetchRecruitments();
                                setJobOfferModal(null);
                            });
                        } else {
                            return ModelService.updateRecruitment(jobOfferModal.recruitmentId, values).then(() => {
                                fetchRecruitments();
                                setJobOfferModal(null);
                            });
                        }
                    }}
                />
            )}
        </VStack>
    );
};

export default ScenarioDetailPage;
